import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Chip from '@material-ui/core/Chip';
import lng from '../../misc/lng';
import { useFontSize } from '../../contexts/FontSizeContext';

const { chiLng } = lng;

const useStyles = makeStyles((theme) => ({
    wrapper: {
        width: '100%',
        marginBottom: theme.spacing(2),
    },
    titleWrapper: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        marginBottom: theme.spacing(1),
    },
    title: {
        fontSize: '16px',
        fontWeight: 'bold',
        color: '#212121',
    },
    helperText: {
        fontSize: '12px',
        color: '#757575',
        fontStyle: 'italic',
    },
    helperTextError: {
        fontSize: '12px',
        color: '#f44336',
        fontStyle: 'italic',
    },
    optionsContainer: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(1),
    },
    optionChip: {
        backgroundColor: '#f5f5f5 !important',
        color: '#212121 !important',
        border: '1px solid #E0E0E0',
        minWidth: 'auto',
        height: '32px',
        '&:hover': {
            backgroundColor: '#e0e0e0 !important',
        },
        '& .MuiChip-label': {
            color: '#212121 !important',
            fontSize: 'inherit !important',
        },
    },
    optionChipSelected: {
        backgroundColor: '#4CAF50 !important',
        color: '#FFFFFF !important',
        border: '1px solid #4CAF50',
        minWidth: 'auto',
        height: '32px',
        '&:hover': {
            backgroundColor: '#45a049 !important',
            border: '1px solid #45a049',
        },
        '& .MuiChip-label': {
            color: '#FFFFFF !important',
            fontSize: 'inherit !important',
        },
    },
    optionChipDisabled: {
        backgroundColor: '#f5f5f5 !important',
        color: '#bdbdbd !important',
        border: '1px solid #E0E0E0',
        textDecoration: 'line-through',
        opacity: 0.5,
        cursor: 'not-allowed',
        minWidth: 'auto',
        height: '32px',
        '&:hover': {
            backgroundColor: '#f5f5f5 !important',
        },
        '& .MuiChip-label': {
            color: '#bdbdbd !important',
            textDecoration: 'line-through',
            fontSize: 'inherit !important',
        },
    },
}));

const SpecSelectionListNew = (props) => {
    const classes = useStyles();
    const { getFontSize } = useFontSize();

    // Dynamic font sizes
    const titleFontSize = getFontSize('bodyText');
    const helperFontSize = getFontSize('caption');
    const chipFontSize = getFontSize('buttonText');

    const handleClick = (oIdx) => {
        props.handleChange(oIdx, props.specIdx);
    };

    const getSelected = (list) => {
        return list.filter(l => l.selected).length;
    };

    const isSpecFulfilled = (spec) => {
        const numOfSelected = getSelected(spec.specOptions);

        // Use SelectionRule if available, otherwise fallback to legacy logic
        if (spec.selectionRule) {
            return spec.selectionRule.isValidQuantity(numOfSelected);
        }

        // Legacy fallback logic
        const { maxQty, minQty } = spec;
        if (maxQty === minQty) {
            return numOfSelected === maxQty;
        } else if (numOfSelected <= maxQty && numOfSelected >= minQty) {
            return true;
        }
        return false;
    };

    const getHelperText = (spec) => {
        const numOfSelected = getSelected(spec.specOptions);
        const isFulfilled = isSpecFulfilled(spec);

        // Use SelectionRule if available
        if (spec.selectionRule) {
            const errorMessage = spec.selectionRule.getValidationError(numOfSelected);
            const displayLabel = spec.selectionRule.getDisplayLabel();

            return (
                <Typography
                    variant="inherit"
                    className={isFulfilled ? classes.helperText : classes.helperTextError}
                    style={{ fontSize: helperFontSize }}
                >
                    {errorMessage || displayLabel}
                </Typography>
            );
        }

        // Legacy fallback logic
        const { maxQty, minQty } = spec;
        if (maxQty === minQty) {
            return (
                <Typography
                    variant="inherit"
                    className={isFulfilled ? classes.helperText : classes.helperTextError}
                    style={{ fontSize: helperFontSize }}
                >
                    {`${chiLng.specLimitHelperTextSpecifyNumberPT1}${maxQty}${chiLng.specLimitHelperTextSpecifyNumberPT2}`}
                </Typography>
            );
        }
        return (
            <Typography
                variant="inherit"
                className={isFulfilled ? classes.helperText : classes.helperTextError}
                style={{ fontSize: helperFontSize }}
            >
                {`${chiLng.specLimitHelperTextSpecifyNumberPT5} ${minQty} ${chiLng.specLimitHelperTextSpecifyNumberPT6} ${maxQty} ${chiLng.specLimitHelperTextSpecifyNumberPT7}`}
            </Typography>
        );
    };

    const getChipClassName = (opt) => {
        if (opt.outOfStock) {
            return classes.optionChipDisabled;
        }
        return opt.selected ? classes.optionChipSelected : classes.optionChip;
    };

    return (
        <div className={classes.wrapper}>
            <div className={classes.titleWrapper}>
                <Typography
                    variant="inherit"
                    className={classes.title}
                    style={{ fontSize: titleFontSize }}
                >
                    {props.spec.specTitle}
                </Typography>
                {getHelperText(props.spec)}
            </div>
            <div className={classes.optionsContainer}>
                {props.spec.specOptions.map((opt, idx) => {
                    if (opt.showMenuToClient === 'hide') {
                        return null;
                    }
                    
                    return (
                        <Chip
                            key={idx}
                            label={opt.specName}
                            onClick={() => {
                                if (opt.outOfStock) return;
                                handleClick(idx);
                            }}
                            className={getChipClassName(opt)}
                            clickable={!opt.outOfStock}
                            disabled={opt.outOfStock}
                            variant="outlined"
                            size="medium"
                            style={{ fontSize: chipFontSize }}
                        />
                    );
                })}
            </div>
        </div>
    );
};

export default SpecSelectionListNew;
